import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, User, Search, Copy, LogOut, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useWallet } from "@/hooks/useWallet";
import { isWhitelistedCreator } from "@/lib/whitelist";

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

const Header = ({ searchQuery = "", onSearchChange }: HeaderProps) => {
  const navigate = useNavigate();
  const { 
    address, 
    formattedAddress, 
    isConnected, 
    isConnecting,
    usdcBalance,
    connect,
    disconnect 
  } = useWallet();

  const handleCopyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      toast.success("Address copied!");
    }
  };

  const handleDisconnect = () => {
    disconnect();
    toast.success("Wallet disconnected");
  };

  const handleConnect = () => {
    connect();
  };

  return (
    <header className="fixed-header border-b border-border bg-background backdrop-blur-sm">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex items-center gap-4 lg:gap-6 h-16">
          {/* Logo - KBETS branding */}
          <div 
            className="flex items-center gap-2 shrink-0 cursor-pointer group" 
            onClick={() => navigate("/")}
          >
            <Flame className="h-6 w-6 text-primary transition-transform duration-300 group-hover:scale-110" />
            <span className="text-2xl font-bold text-foreground tracking-tight">
              KB<span className="text-primary">E</span>TS
            </span>
          </div>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-md lg:max-w-lg">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search markets..."
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="pl-10 bg-card/50 border-border hover:border-primary/50 focus:border-primary transition-all"
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Button 
              size="sm" 
              variant="ghost"
              className="text-muted-foreground hover:text-foreground hover:bg-card"
              onClick={() => navigate("/docs")}
            >
              Docs
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-muted-foreground hover:text-foreground hover:bg-card"
              onClick={() => navigate("/esport-stats")}
            >
              Esport Stats
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-muted-foreground hover:text-foreground hover:bg-card"
              onClick={() => navigate("/portfolio")}
            >
              Profile
            </Button>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-auto">
            {isWhitelistedCreator(address) && (
              <Button 
                size="sm" 
                className="hidden md:flex bg-primary hover:bg-primary/90 text-background font-semibold"
                onClick={() => navigate("/create-market")}
              >
                Create Market
              </Button>
            )}
            
            {!isConnected ? (
              <Button 
                size="sm" 
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-background"
                onClick={handleConnect} 
                disabled={isConnecting}
              >
                <Wallet className="w-4 h-4 mr-2" />
                {isConnecting ? "Connecting..." : "Connect"}
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="border-primary/50">
                    <Wallet className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">{formattedAddress}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 bg-card border-border z-50">
                  {/* Balance */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">USDT Balance</span>
                      <span className="text-base font-bold text-foreground">
                        ${parseFloat(usdcBalance).toFixed(2)}
                      </span>
                    </div>
                    <button 
                      onClick={handleCopyAddress}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors mt-2"
                    >
                      <span className="font-mono">{formattedAddress}</span>
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="p-1">
                    <DropdownMenuItem 
                      onClick={handleDisconnect}
                      className="cursor-pointer text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Disconnect
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
